'use client'

import { useEffect, useRef, useState } from 'react'
import dynamic from 'next/dynamic'

import { firstNameOf } from '@/lib/name'
import { SCREEN_DIMENSIONS, listLabels } from '@/lib/careers/screening'
import { useCareersVoice } from './useCareersVoice'
import { VOICE_OPENER } from './vapi-assistant'

// WebGL blob — client-only, loaded lazily so the chat never waits on it.
const Orb = dynamic(() => import('@/components/ui/orb/Orb'), { ssr: false })

function MicIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden>
      <rect x="9" y="3" width="6" height="11" rx="3" fill="currentColor" />
      <path
        d="M5 11a7 7 0 0 0 14 0M12 18v3"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  )
}

function MicOffIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden>
      <rect x="9" y="3" width="6" height="11" rx="3" fill="currentColor" />
      <path
        d="M5 11a7 7 0 0 0 14 0M12 18v3"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M4.5 3.5l15 17"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
      />
    </svg>
  )
}

// Send glyphs in the house dot language. Idle shows a plain up arrow; once
// the candidate types, the dots morph pixel-by-pixel into an ↵ enter arrow.
const SEND_IDLE = [
  [0, 0, 1, 0, 0],
  [0, 1, 1, 1, 0],
  [1, 0, 1, 0, 1],
  [0, 0, 1, 0, 0],
  [0, 0, 1, 0, 0],
]
const SEND_READY = [
  [0, 0, 0, 0, 1],
  [0, 0, 0, 0, 1],
  [0, 1, 0, 0, 1],
  [1, 1, 1, 1, 1],
  [0, 1, 0, 0, 0],
]

function SendDotsIcon({ ready }) {
  return (
    <span className="flex flex-col gap-px">
      {SEND_IDLE.map((row, r) => (
        <span key={r} className="flex gap-px">
          {row.map((_, c) => {
            const on = (ready ? SEND_READY : SEND_IDLE)[r][c]
            return (
              <span
                key={c}
                style={{
                  opacity: on ? 1 : 0.22,
                  transitionDelay: `${(r * 5 + c) * 12}ms`,
                }}
                className="inline-block size-0.75 shrink-0 rounded-full bg-current transition-opacity duration-300"
              />
            )
          })}
        </span>
      ))}
    </span>
  )
}

// Voice conversation icon in the house dot language: a pixel-dot waveform,
// columns rising toward the center like speech.
function DotWaveIcon({ className }) {
  const columns = [
    { x: 4, dots: 2 },
    { x: 8, dots: 4 },
    { x: 12, dots: 5 },
    { x: 16, dots: 3 },
    { x: 20, dots: 2 },
  ]
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden>
      {columns.flatMap(({ x, dots }) =>
        Array.from({ length: dots }, (_, i) => {
          const y = 12 + (i - (dots - 1) / 2) * 3.4
          return (
            <circle
              key={`${x}-${i}`}
              cx={x}
              cy={y}
              r="1.25"
              fill="currentColor"
            />
          )
        }),
      )}
    </svg>
  )
}

// A brief radial burst of brand pixels for the completion moment.
const BURST_COLORS = ['#C9A84C', '#34d399', '#ffffff', '#d9bd65']

function PixelBurst({ count = 48 }) {
  const [fired, setFired] = useState(false)
  const dotsRef = useRef(null)
  if (!dotsRef.current) {
    dotsRef.current = Array.from({ length: count }, (_, i) => {
      const angle = (i / count) * Math.PI * 2 + (i % 3) * 0.4
      const dist = 60 + (i % 5) * 26
      return {
        x: Math.cos(angle) * dist,
        y: Math.sin(angle) * dist,
        color: BURST_COLORS[i % BURST_COLORS.length],
        delay: (i % 8) * 24,
        size: 3 + (i % 3),
      }
    })
  }
  useEffect(() => {
    const raf = requestAnimationFrame(() => setFired(true))
    return () => cancelAnimationFrame(raf)
  }, [])

  return (
    <span
      aria-hidden
      className="pointer-events-none relative inline-block size-6 motion-reduce:hidden"
    >
      {dotsRef.current.map((d, i) => (
        <span
          key={i}
          style={{
            background: d.color,
            width: d.size,
            height: d.size,
            transform: fired
              ? `translate(${d.x}px, ${d.y}px) scale(0.4)`
              : 'translate(0, 0) scale(1)',
            opacity: fired ? 0 : 1,
            transitionDelay: `${d.delay}ms`,
          }}
          className="absolute top-1/2 left-1/2 rounded-full transition-all duration-1000 ease-out"
        />
      ))}
    </span>
  )
}

// 5x5 dot-matrix check for the what-happens-next timeline.
const TAKEOVER_CHECK = [
  [0, 0, 0, 0, 1],
  [0, 0, 0, 1, 0],
  [1, 0, 1, 0, 0],
  [0, 1, 0, 0, 0],
  [0, 0, 0, 0, 0],
]

function StepPlate({ done }) {
  return (
    <span
      className={`flex shrink-0 flex-col gap-[2px] rounded-full border bg-neutral-950/60 p-[5px] ${
        done
          ? 'border-emerald-400/30 text-emerald-400 shadow-[0_0_10px_rgba(52,211,153,0.3)]'
          : 'animate-pulse border-white/10 text-white'
      }`}
      aria-hidden
    >
      {TAKEOVER_CHECK.map((row, r) => (
        <span key={r} className="flex gap-[2px]">
          {row.map((on, c) => (
            <span
              key={c}
              style={{ opacity: done ? (on ? 1 : 0.12) : 0.18 }}
              className="inline-block size-0.5 rounded-full bg-current"
            />
          ))}
        </span>
      ))}
    </span>
  )
}

// The transcript gracefully clears into this once the screen completes —
// during the live session (server-side completion is handled by the page).
function CompletionPanel({ firstName }) {
  const steps = [
    { label: 'Interview received', done: true },
    { label: 'The team reviews every screen', done: false },
    { label: "You'll hear from us by email", done: false },
  ]
  return (
    <div className="mx-auto mt-10 w-full max-w-md">
      <div className="rounded-3xl border border-kingdom-gold/30 bg-kingdom-gold/[0.06] p-8 text-center">
        <div className="flex justify-center pt-4 pb-8">
          <PixelBurst />
        </div>
        <h2 className="font-display text-2xl font-medium">
          That&apos;s everything{firstName ? `, ${firstName}` : ''}.
        </h2>
        <p className="mt-3 text-sm leading-6 text-white/70">
          Thanks — the team reviews every screen and you&apos;ll hear from us by
          email.
        </p>
        <ul className="mx-auto mt-6 max-w-xs space-y-3 text-left">
          {steps.map((s) => (
            <li key={s.label} className="flex items-center gap-3 text-sm">
              <StepPlate done={s.done} />
              <span className={s.done ? 'text-white' : 'text-white/60'}>
                {s.label}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

export function ScreenChat({
  token,
  candidateName = null,
  postingTitle = null,
  interviewFocus = null,
  salaryRange = null,
  remaining: remainingProp = null,
  started = false,
}) {
  const startRemaining = Array.isArray(remainingProp)
    ? remainingProp
    : [...SCREEN_DIMENSIONS]

  // Invited candidates get greeted by name; a candidate returning to an
  // unfinished screen gets pointed at exactly what's left instead of being
  // asked to start over. (No initialComplete gate here — the page only ever
  // renders this component for status === 'interview-invited'; every later
  // status gets the server-rendered thank-you card instead.)
  const firstName = firstNameOf(candidateName)
  const opener =
    started && startRemaining.length > 0 && startRemaining.length < SCREEN_DIMENSIONS.length
      ? `Welcome back${firstName ? `, ${firstName}` : ''} — everything you've shared is saved. Still left to cover: ${listLabels(startRemaining)}. Pick up whenever you're ready — type below, or tap the orb to continue by voice.`
      : firstName
        ? `Hi ${firstName} — thanks for making the time. This will run about ten minutes. Let's start simple: tell me a bit about yourself, and what drew you to this role.`
        : VOICE_OPENER

  const [messages, setMessages] = useState([
    { role: 'interviewer', text: opener },
  ])
  const [input, setInput] = useState('')
  const [busy, setBusy] = useState(false)
  const [complete, setComplete] = useState(false)
  const [remaining, setRemaining] = useState(startRemaining)
  const [error, setError] = useState(null)

  // Dictation (speech-to-text into the composer — the candidate reviews
  // before sending, exactly like ChatGPT's mic).
  const [dictationSupported, setDictationSupported] = useState(false)
  const [listening, setListening] = useState(false)
  const recognitionRef = useRef(null)
  const dictationBaseRef = useRef('')

  const endRef = useRef(null)
  const inputRef = useRef(null)
  const autoOpenedRef = useRef(false)

  // Auto-size the composer to its content (dictation can pour in whole
  // paragraphs) — grow up to a cap, then scroll inside.
  useEffect(() => {
    const el = inputRef.current
    if (!el) return
    const prev = el.style.height
    el.style.height = 'auto'
    const next = `${Math.min(el.scrollHeight, 160)}px`
    el.style.height = prev
    void el.offsetHeight
    el.style.height = next
  }, [input])

  // Live Vapi voice interview — presented as a centered modal with the AI
  // orb. The transcript keeps accruing in the chat underneath.
  const vapi = useCareersVoice({
    token,
    candidateName,
    postingTitle,
    interviewFocus,
    salaryRange,
    onTurn: (turn) =>
      setMessages((m) => {
        // Vapi finalizes speech in fragments — grow the current speaker's
        // bubble instead of stacking fragment bubbles.
        const last = m[m.length - 1]
        if (last?.via === 'voice' && last.role === turn.role) {
          const copy = [...m]
          copy[copy.length - 1] = { ...last, text: `${last.text} ${turn.text}` }
          return copy
        }
        return [...m, { role: turn.role, text: turn.text, via: 'voice' }]
      }),
    onComplete: () => setComplete(true),
    // The call ended with areas still uncovered (deliberate stop or a drop).
    // No scary red text when everything saved — an interviewer bubble names
    // exactly what's left and how to continue.
    onIncomplete: ({ remaining: rem, dropped }) => {
      if (Array.isArray(rem) && rem.length > 0) setRemaining(rem)
      const lead = dropped
        ? 'The call dropped, but everything you said is saved.'
        : 'Good progress — everything you shared is saved.'
      const next =
        Array.isArray(rem) && rem.length > 0
          ? ` Still left to cover: ${listLabels(rem)}. Type your answer below, or tap the orb and we'll pick up right there.`
          : ' Continue below whenever you’re ready.'
      setMessages((m) => [...m, { role: 'interviewer', text: `${lead}${next}` }])
    },
    onError: (msg) => setError(msg),
  })
  const conversationOpen = vapi.callState !== 'idle'

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
  }, [messages, busy])

  useEffect(() => {
    setDictationSupported(
      Boolean(window.SpeechRecognition || window.webkitSpeechRecognition),
    )
    return () => recognitionRef.current?.abort?.()
  }, [])

  // Voice-first: a fresh invite lands straight in the voice modal — the
  // candidate can close it any time, and the text composer underneath is
  // always the silent fallback.
  useEffect(() => {
    if (autoOpenedRef.current || started || complete) return
    if (!vapi.available) return
    autoOpenedRef.current = true
    vapi.start({ remaining: startRemaining, resuming: false })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vapi.available])

  function stopDictation() {
    recognitionRef.current?.stop?.()
  }

  function startDictation() {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition
    if (!SR || busy || complete || listening) return
    setError(null)
    dictationBaseRef.current = input ? `${input.trim()} ` : ''

    const recognition = new SR()
    recognition.lang = navigator.language?.startsWith('en')
      ? navigator.language
      : 'en-US'
    recognition.interimResults = true
    recognition.continuous = true

    recognition.onresult = (e) => {
      let text = ''
      for (const result of e.results) text += result[0].transcript
      // Words land in the composer as they're spoken — review, edit, send.
      setInput(`${dictationBaseRef.current}${text}`.slice(0, 2000))
    }
    recognition.onend = () => {
      setListening(false)
      recognitionRef.current = null
    }
    recognition.onerror = (e) => {
      setListening(false)
      if (e.error === 'not-allowed' || e.error === 'service-not-allowed') {
        setError(
          'Microphone access was blocked — allow it in your browser, or type instead.',
        )
      } else if (e.error !== 'aborted' && e.error !== 'no-speech') {
        setError('Dictation hiccuped — try again, or type instead.')
      }
    }

    recognitionRef.current = recognition
    setListening(true)
    try {
      recognition.start()
    } catch {
      setListening(false)
    }
  }

  function startConversation() {
    setError(null)
    stopDictation()
    // A session with history resumes at the first uncovered area instead of
    // greeting the candidate from scratch.
    vapi.start({ remaining, resuming: started || messages.length > 1 })
  }

  async function send(e, overrideMessage) {
    e?.preventDefault()
    stopDictation()
    const message = (overrideMessage ?? input).trim()
    if (!message || busy || complete) return
    setError(null)
    setBusy(true)
    setMessages((m) => [...m, { role: 'candidate', text: message }])
    setInput('')

    try {
      const res = await fetch('/api/careers/interview/turn', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, message }),
      })

      // Pre-stream failures (validation, closed interview) come back as JSON.
      const contentType = res.headers.get('content-type') || ''
      if (!contentType.includes('text/event-stream')) {
        const data = await res.json().catch(() => ({}))
        // A stale tab can still be open after the interview finished in
        // another tab (or via voice) — the route's raw 'closed' code isn't
        // candidate-facing copy, so translate it before it hits the screen.
        throw new Error(
          data.error === 'closed'
            ? "This interview has already been completed — you're all set."
            : data.error || 'Something went wrong',
        )
      }

      // The interviewer's reply streams in live as `delta` events.
      setMessages((m) => [
        ...m,
        { role: 'interviewer', text: '', streaming: true },
      ])
      const reader = res.body.getReader()
      const decoder = new TextDecoder()
      let pending = ''
      let sawDone = false

      const appendDelta = (text) =>
        setMessages((m) => {
          const copy = [...m]
          const last = copy[copy.length - 1]
          copy[copy.length - 1] = { ...last, text: last.text + text }
          return copy
        })

      for (;;) {
        const { done, value } = await reader.read()
        if (done) break
        pending += decoder.decode(value, { stream: true })
        const events = pending.split('\n\n')
        pending = events.pop()
        for (const raw of events) {
          const line = raw.trim()
          if (!line.startsWith('data: ')) continue
          const evt = JSON.parse(line.slice(6))
          if (evt.type === 'delta') appendDelta(evt.text)
          else if (evt.type === 'error') throw new Error(evt.error)
          else if (evt.type === 'done') {
            sawDone = true
            if (Array.isArray(evt.remaining)) setRemaining(evt.remaining)
            // Reconcile with the authoritative final reply.
            setMessages((m) => {
              const copy = [...m]
              copy[copy.length - 1] = { role: 'interviewer', text: evt.reply }
              return copy
            })
            if (evt.complete) setComplete(true)
          }
        }
      }
      if (!sawDone)
        throw new Error('Connection interrupted — please try again.')
    } catch (err) {
      setError(err.message)
      // Give the candidate their message back so nothing is lost.
      setInput(message)
      setMessages((m) => {
        let copy = [...m]
        if (copy[copy.length - 1]?.streaming) copy = copy.slice(0, -1)
        if (copy[copy.length - 1]?.role === 'candidate') copy = copy.slice(0, -1)
        return copy
      })
    } finally {
      setBusy(false)
    }
  }

  // Interview complete — the transcript clears into the thank-you moment.
  if (complete) {
    return <CompletionPanel firstName={firstName} />
  }

  return (
    <div className="mt-6 flex min-h-0 flex-1 flex-col">
      {/* Transcript scrolls internally; the composer below stays pinned. */}
      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto rounded-3xl border border-white/10 bg-white/[0.03] p-5 sm:p-7">
        {messages
          .filter((m) => m.text || !m.streaming)
          .map((m, i) => (
            <div
              key={i}
              className={
                m.role === 'candidate' ? 'flex justify-end' : 'flex justify-start'
              }
            >
              <div
                className={
                  m.role === 'candidate'
                    ? 'max-w-[85%] rounded-2xl rounded-br-sm bg-kingdom-gold/90 px-4 py-3 text-sm leading-relaxed text-neutral-950'
                    : 'max-w-[85%] rounded-2xl rounded-bl-sm bg-white/8 px-4 py-3 text-sm leading-relaxed text-neutral-100'
                }
              >
                {m.text}
                {m.streaming && m.text ? (
                  <span className="ml-0.5 inline-block h-3.5 w-[2px] animate-pulse bg-kingdom-gold align-middle" />
                ) : null}
              </div>
            </div>
          ))}
        {busy && !messages[messages.length - 1]?.text && (
          <div className="flex justify-start">
            <div className="rounded-2xl rounded-bl-sm bg-white/8 px-4 py-3 text-sm text-neutral-400">
              <span className="inline-flex gap-1">
                <span className="animate-pulse">·</span>
                <span className="animate-pulse [animation-delay:150ms]">·</span>
                <span className="animate-pulse [animation-delay:300ms]">·</span>
              </span>
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      {error && (
        <p className="mt-3 text-center text-sm text-red-400">{error}</p>
      )}

      {/* The composer — ChatGPT-style: the text spans the field's full width
          and wraps fluidly; the actions live in their own row underneath,
          floated to the bottom right. The field grows with its content.
          Always present — the silent fallback under the voice-first modal. */}
      <form
        onSubmit={send}
        className="mt-4 rounded-[28px] border border-white/15 bg-white/5 px-2 pt-2 pb-2 transition focus-within:border-kingdom-gold/60"
      >
        <textarea
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            // Enter sends, Shift+Enter breaks a line — same as the big chat apps.
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              send(e)
            }
          }}
          disabled={busy}
          maxLength={2000}
          rows={1}
          placeholder={
            listening
              ? 'Listening — your words appear here…'
              : 'Write your answer…'
          }
          className="block max-h-40 w-full resize-none overflow-y-auto bg-transparent px-3 py-2 text-sm leading-6 text-white transition-[height] duration-150 ease-[cubic-bezier(.645,.045,.355,1)] placeholder:text-neutral-500 focus:outline-none disabled:opacity-50 motion-reduce:transition-none"
        />
        <div className="mt-1 flex items-center justify-end gap-1.5">
          {dictationSupported && (
            <button
              type="button"
              onClick={listening ? stopDictation : startDictation}
              disabled={busy}
              aria-label={
                listening ? 'Stop dictation' : 'Dictate your answer'
              }
              title="Dictate"
              className={
                listening
                  ? 'relative flex size-10 shrink-0 items-center justify-center rounded-full bg-red-500/90 text-white transition after:absolute after:inset-0 after:animate-ping after:rounded-full after:bg-red-500/40'
                  : 'flex size-10 shrink-0 items-center justify-center rounded-full text-neutral-300 transition hover:bg-white/10 hover:text-kingdom-gold disabled:opacity-40'
              }
            >
              <MicIcon className="size-5" />
            </button>
          )}
          {vapi.available && (
            <button
              type="button"
              onClick={startConversation}
              disabled={busy}
              aria-label="Start a voice conversation"
              title="Voice conversation"
              className="group relative flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-full border border-kingdom-gold/40 bg-neutral-950 transition hover:border-kingdom-gold disabled:opacity-40"
            >
              {/* The orb idles behind the icon — always alive. */}
              <span className="pointer-events-none absolute inset-0">
                <Orb
                  hue={45}
                  hoverIntensity={1.6}
                  rotateOnHover
                  backgroundColor="#0a0a0a"
                />
              </span>
              <DotWaveIcon className="pointer-events-none relative z-10 size-5 text-white drop-shadow-[0_0_6px_rgba(0,0,0,0.9)] transition-colors group-hover:text-kingdom-gold" />
            </button>
          )}
          <button
            type="submit"
            disabled={busy || !input.trim()}
            aria-label="Send"
            title="Send"
            className={
              input.trim()
                ? 'flex size-10 shrink-0 items-center justify-center rounded-full bg-kingdom-gold text-white transition hover:bg-kingdom-gold/90 disabled:opacity-40'
                : 'pointer-events-none flex size-10 shrink-0 items-center justify-center rounded-full border border-white/10 bg-white/5 text-neutral-400'
            }
          >
            <SendDotsIcon ready={Boolean(input.trim())} />
          </button>
        </div>
      </form>

      {/* Conversational mode — the AI orb takes center stage. Dismissing ends
          the session and returns to the transcript (dictate, type, or restart
          the conversation any time). */}
      {conversationOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-950/80 backdrop-blur-md"
          onClick={vapi.callState === 'finalizing' ? undefined : vapi.stop}
        >
          <div
            className="relative mx-4 flex w-full max-w-sm flex-col items-center rounded-3xl border border-white/10 bg-neutral-900/90 px-8 pt-10 pb-8 text-center shadow-[0_40px_120px_rgba(0,0,0,0.6)]"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Finalization takes a few seconds and MUST land — hide the exit
                so it can't be mistaken for a frozen screen with a way out. */}
            {vapi.callState !== 'finalizing' && (
              <button
                type="button"
                onClick={vapi.stop}
                aria-label="End conversation and view transcript"
                className="absolute top-4 right-4 flex size-9 items-center justify-center rounded-full border border-white/15 text-sm text-neutral-400 transition hover:border-red-400/50 hover:text-red-300"
              >
                ✕
              </button>
            )}

            <div className="relative size-48 sm:size-56">
              <Orb
                hue={45}
                hoverIntensity={2.4}
                rotateOnHover
                forceHoverState={vapi.assistantSpeaking}
                backgroundColor="#171717"
              />
            </div>

            <p className="mt-6 font-display text-lg text-white">
              {vapi.callState === 'connecting'
                ? 'Connecting…'
                : vapi.callState === 'finalizing'
                  ? 'Wrapping up your interview…'
                  : vapi.assistantSpeaking
                    ? 'Interviewer speaking…'
                    : vapi.muted
                      ? 'Muted — tap the mic to speak'
                      : 'Listening — go ahead'}
            </p>

            {/* Live caption of whoever is talking right now — or, while
                finalizing, visible activity so the wrap-up never reads as a
                frozen screen. */}
            <p className="mt-3 min-h-10 text-sm leading-relaxed text-neutral-400 italic">
              {vapi.callState === 'finalizing' ? (
                <span className="not-italic">
                  Saving your answers, checking every question is covered
                  <span className="ml-1 inline-flex gap-0.5">
                    <span className="animate-pulse">·</span>
                    <span className="animate-pulse [animation-delay:150ms]">·</span>
                    <span className="animate-pulse [animation-delay:300ms]">·</span>
                  </span>
                </span>
              ) : vapi.liveCaption?.text ? (
                `${vapi.liveCaption.text}…`
              ) : (
                ''
              )}
            </p>

            {/* Mic mute — step away, cough, think out loud in private. */}
            <button
              type="button"
              onClick={vapi.toggleMute}
              aria-label={vapi.muted ? 'Unmute microphone' : 'Mute microphone'}
              aria-pressed={vapi.muted}
              title={vapi.muted ? 'Unmute' : 'Mute'}
              className={
                vapi.muted
                  ? 'mt-4 flex size-12 items-center justify-center rounded-full bg-red-500/90 text-white transition hover:bg-red-500'
                  : 'mt-4 flex size-12 items-center justify-center rounded-full border border-white/15 text-neutral-300 transition hover:border-kingdom-gold hover:text-kingdom-gold'
              }
            >
              {vapi.muted ? (
                <MicOffIcon className="size-5" />
              ) : (
                <MicIcon className="size-5" />
              )}
            </button>

            <p className="mt-4 text-[0.68rem] text-neutral-500">
              Talk naturally — the full transcript is saved to your interview.
              Close this window any time to read it or answer by text.
            </p>
          </div>
        </div>
      )}
    </div>
  )
}
